import { getChordNotes } from './chordNotes';
import { getChromaticScale } from './diatonicChords';

/**
 * The two notes a rhythm's left hand alternates under a held chord.
 *
 * A block chord says what the harmony is; it doesn't say what playing it sounds like. The bass a
 * player puts under a held C is C-G-C-G — the chord's root, and its fifth reached down for rather
 * than up, so the hand rocks between two notes with the lower one on the offbeat. What makes that
 * generalise is that the fifth is the one interval every chord has: the third moves with the
 * quality and the seventh comes and goes, but a chord that has a body at all has a fifth in it.
 *
 * Deliberately a leaf. It reads intervals and hands back MIDI numbers, and knows nothing about
 * bars, tempo or the clip shape — chordBarsMidi.js imports this, so this can't import that.
 */

/**
 * The intervals that count as the chord's fifth, in the order they are looked for.
 *
 * Perfect first, then the two a quality can alter it to: dim and m7b5 flatten it to a 6, aug raises
 * it to an 8. Taking the chord's own fifth rather than always a perfect one is what keeps the bass
 * inside the chord — a perfect fifth under a Cdim would sound the G the chord went out of its way
 * not to have.
 */
const FIFTHS = [7, 6, 8];

/** A note name's pitch class, 0 for C. Both chromatic scales start at C, so the index is it. */
const getPitchClass = (note) => {
  const index = getChromaticScale(note).indexOf(note);

  return index === -1 ? null : index;
};

/**
 * The bass pair one chord symbol alternates, as MIDI numbers: [root, the fifth below it].
 *
 * The fifth is voiced *under* the root rather than over it, which is where the hand goes for it and
 * what gives the pattern its shape — the beat falls on the higher note. A slash chord starts on the
 * bass the chart asked for, since that is the whole point of writing one, and the fifth still comes
 * from the chord itself: G/B alternates B with G's D, not with B's own.
 *
 * @param {string} chord - A chord symbol
 * @param {number} bassOctaveMidi - MIDI number of the C the root is voiced from
 * @returns {number[]|null} [root, fifth below it], or null for a symbol with no readable root
 */
export const getRhythmBassPair = (chord, bassOctaveMidi) => {
  const { root, intervals, bassNote } = getChordNotes(chord);
  const rootClass = getPitchClass(root);

  if (rootClass === null) return null;

  // Whatever this chord calls a fifth. Every quality has one of the three; the fallback is for a
  // symbol the parser read as something with no fifth at all rather than for any chord in the table.
  const fifth = FIFTHS.find((interval) => intervals.includes(interval)) ?? 7;

  // A slash chord's bass is where the pattern starts — that is the whole point of writing one
  const bassClass = (bassNote ? getPitchClass(bassNote) : null) ?? rootClass;
  const fifthClass = (rootClass + fifth) % 12;

  // How far down to the nearest fifth. A bass already sitting on it drops a whole octave rather
  // than staying put, which is what the pattern does under a chord written over its own fifth.
  const drop = (bassClass - fifthClass + 12) % 12 || 12;

  return [bassOctaveMidi + bassClass, bassOctaveMidi + bassClass - drop];
};
